import { parseFuturesConfiguration, type FuturesConfigurationDraft } from '@/features/strategyBacktests/futuresConfigurationDraft';
import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import type { EntityConfig } from '@/components/UI/EntitySelectionPanel';
import type { AeroOptionReturnSelectionPolicy, BaseExitRole, StopLossBasis, TradeLogicPriceFillModelV2 } from '@einhaus/project314-types';
import { getFrontendSafeDevelopmentCutoffDateTime } from '@/utils/researchWindowBoundaries';
import type { AeroOptimizationJobInput, AeroOptimizationJobDetailResult } from '../api';
import {
    buildAeroParameterRegistryBlockingReasons,
    type AeroParameterFamilySupportKey,
    type AeroParameterSpaceRegistryPlan
} from './aeroParameterSpaceRegistry';

type AeroParameterSpaceConfig = AeroOptimizationJobInput['parameterSpaceConfig'];
type AeroAutoPartialExitsConfig = NonNullable<AeroParameterSpaceConfig['profitTargetAutoPartialExits']>;
type AeroJobDetail = NonNullable<AeroOptimizationJobDetailResult['job']>;

const DEFAULT_AUTO_PARTIAL_MAX_LEVELS = 2;
const DEFAULT_AUTO_PARTIAL_MIN_SPACING_PERCENT = 0;
const DEFAULT_AUTO_PARTIAL_CAP_PER_TARGET = 200;
const MAX_AUTO_PARTIAL_ESTIMATE_LEVELS = 250;
const DEFAULT_START_DATE_TIME = '2020-08-03 09:30:00';
const MARKET_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MARKET_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
const MARKET_DATE_TIME_WITHOUT_SECONDS_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;

export const AERO_MIN_HOLD_DAYS = 30;
export const AERO_MAX_HOLD_DAYS = 365;

export type AeroPartialExitMode = 'none' | 'manual' | 'auto';
export type AeroUniverseMode = 'entities' | 'screener';
export type AeroScreenerUniverseEntityType = 'Asset' | 'Cryptocurrency' | 'AssetIndex';

export interface AeroPartialExitDraft {
    atProfit: string;
    exitPercentage: string;
}

export interface AeroProfitFloorDraft {
    triggerPercentage: string;
    sellAtPercentage: string;
}

export interface AeroAutoPartialExitState {
    atProfitFrom: string;
    atProfitTo: string;
    atProfitStep: string;
    exitPercentages: number[];
    maxLevels: number;
    minSpacingPercent: string;
    capPerTarget: string;
    includeRemainderAtTarget: boolean;
}

export interface AeroAutoPartialTriggerPreview {
    /** Profit levels the from/to/step range expands into. */
    triggers: number[];
    /** True when the expansion hit the estimate ceiling and the list was clipped. */
    truncated: boolean;
    /** Validation message when the range is malformed; null while empty or valid. */
    error: string | null;
    /** True once any of from/to/step has been entered. */
    started: boolean;
}

export interface AeroOptimizationJobInputState {
    futuresConfigurations?: FuturesConfigurationDraft[];
    jobName: string;
    description: string;
    universeMode: AeroUniverseMode;
    entities: EntityConfig[];
    /** Entity list the entities were picked from, for the selection panel. AERO jobs pin entities only. */
    entityListId: number | null;
    screenerRuleId: number | null;
    screenerEntityTypes: AeroScreenerUniverseEntityType[];
    startDateTime: string;
    endDateTime: string;
    stopLossEnabled: boolean;
    stopLossBasis: StopLossBasis;
    stopLossPercentages: number[];
    trailingStopEnabled: boolean;
    trailingActivationProfits: number[];
    trailingByPercentages: number[];
    profitTargetEnabled: boolean;
    profitTargetPercentages: number[];
    partialExitMode: AeroPartialExitMode;
    manualPartialExits: AeroPartialExitDraft[];
    autoPartialExits: AeroAutoPartialExitState;
    profitFloorEnabled: boolean;
    profitFloors: AeroProfitFloorDraft[];
    maxHoldBarsEnabled: boolean;
    maxHoldBars: number[];
    includeNoTimeStop: boolean;
    priceFillModel: TradeLogicPriceFillModelV2;
    optionBuyingStrategyIds: number[];
    optionReturnSelectionPolicy: AeroOptionReturnSelectionPolicy;
    tradeSizeDollars: string;
    includeStrategyExitComplement: boolean;
    maxTradeDurationDays: string;
    /** Percent 0-100 as text; empty uses the AERO orchestrator default */
    maxFailedUnitPercent: string;
    /** Minimum executable option price as text; empty means no job-level floor */
    minOptionPrice: string;
    deleteTradesAfterCompletion: boolean;
    enableDebugLogging: boolean;
}

export type AeroSearchSpaceTier = 'empty' | 'quick' | 'standard' | 'large' | 'huge';

export interface AeroSearchSpaceFamilyRow {
    key: string;
    label: string;
    variants: number;
    detail: string | null;
}

export interface AeroSearchSpaceEstimate {
    families: AeroSearchSpaceFamilyRow[];
    combinations: number;
    entityCount: number | null;
    simulations: number | null;
    tier: AeroSearchSpaceTier;
    tierLabel: string;
}

export interface AeroOptimizationJobInputPlan {
    input: AeroOptimizationJobInput | null;
    blockingReasons: string[];
    searchSpace: AeroSearchSpaceEstimate;
    parameterFamilies: string[];
}

export interface AeroBaseExitPolicy {
    role: BaseExitRole | null;
    canChoose: boolean;
    includeStrategyExitComplement: boolean;
    helpText: string;
    blockingReason: string | null;
}

export const resolveAeroBaseExitPolicy = (role: BaseExitRole | null, requestedInclude: boolean): AeroBaseExitPolicy => {
    switch (role) {
        case 'strategy_core':
            return {
                role,
                canChoose: false,
                includeStrategyExitComplement: true,
                helpText: 'Required core strategy exits are included in every AERO candidate.',
                blockingReason: null
            };
        case 'optional_overlay':
            return {
                role,
                canChoose: true,
                includeStrategyExitComplement: requestedInclude,
                helpText: 'Choose whether fixed non-AERO strategy exits are combined with this job’s PT/SL candidates.',
                blockingReason: null
            };
        case 'placeholder':
            return {
                role,
                canChoose: false,
                includeStrategyExitComplement: false,
                helpText: 'This base exit is a benchmark placeholder and is never merged into AERO candidates.',
                blockingReason: null
            };
        default:
            return {
                role: null,
                canChoose: false,
                includeStrategyExitComplement: false,
                helpText: 'Classify the strategy’s base exit before creating AERO jobs.',
                blockingReason: 'Classify the strategy’s base exit as core, optional, or placeholder before creating an AERO job.'
            };
    }
};

interface ActiveFamilies {
    stopLoss: boolean;
    trailing: boolean;
    profitTarget: boolean;
    manualPartials: boolean;
    autoPartials: boolean;
    profitFloor: boolean;
    maxHoldBars: boolean;
}

export const createDefaultAutoPartialExitState = (): AeroAutoPartialExitState => ({
    atProfitFrom: '',
    atProfitTo: '',
    atProfitStep: '',
    exitPercentages: [],
    maxLevels: DEFAULT_AUTO_PARTIAL_MAX_LEVELS,
    minSpacingPercent: '',
    capPerTarget: '',
    includeRemainderAtTarget: true
});

export const createDefaultManualPartialExit = (): AeroPartialExitDraft => ({
    atProfit: '',
    exitPercentage: ''
});

export const createDefaultProfitFloor = (): AeroProfitFloorDraft => ({
    triggerPercentage: '',
    sellAtPercentage: ''
});

const buildDefaultDateWindow = (): { startDateTime: string; endDateTime: string } => ({
    // Default AERO's development window to end inside the training window even when the backend cutoff lags the browser.
    // See docs/plans/OUT_OF_SAMPLE_HOLDOUT_METHODOLOGY_PROPOSAL.md.
    startDateTime: normalizeMarketDateTime(DEFAULT_START_DATE_TIME) ?? DEFAULT_START_DATE_TIME,
    endDateTime: getFrontendSafeDevelopmentCutoffDateTime()
});

export const createDefaultAeroOptimizationJobInputState = (): AeroOptimizationJobInputState => {
    const { startDateTime, endDateTime } = buildDefaultDateWindow();

    return {
        jobName: '',
        description: '',
        universeMode: 'entities',
        entities: [],
        entityListId: null,
        screenerRuleId: null,
        screenerEntityTypes: [],
        startDateTime,
        endDateTime,
        stopLossEnabled: true,
        stopLossBasis: 'openingPremium',
        stopLossPercentages: [5, 8],
        trailingStopEnabled: false,
        trailingActivationProfits: [],
        trailingByPercentages: [],
        profitTargetEnabled: true,
        profitTargetPercentages: [10, 15, 20],
        partialExitMode: 'none',
        manualPartialExits: [],
        autoPartialExits: createDefaultAutoPartialExitState(),
        profitFloorEnabled: false,
        profitFloors: [],
        maxHoldBarsEnabled: false,
        maxHoldBars: [],
        includeNoTimeStop: true,
        priceFillModel: 'observed_close',
        optionBuyingStrategyIds: [],
        optionReturnSelectionPolicy: 'upper_winsorized_v1',
        tradeSizeDollars: '',
        includeStrategyExitComplement: true,
        maxTradeDurationDays: '90',
        maxFailedUnitPercent: '',
        minOptionPrice: '',
        deleteTradesAfterCompletion: false,
        enableDebugLogging: false
    };
};

export const buildAeroOptimizationJobInputPlan = ({
    registryPlan,
    state,
    strategyId
}: {
    registryPlan?: AeroParameterSpaceRegistryPlan | null;
    state: AeroOptimizationJobInputState;
    strategyId: number | null;
}): AeroOptimizationJobInputPlan => {
    const families = resolveActiveFamilies(state);
    const parameterSpaceConfig = buildParameterSpaceConfig(state, families);
    const parameterFamilies = getParameterFamilies(parameterSpaceConfig);
    const searchSpace = buildSearchSpaceEstimate(state, families);

    let futuresError: string | null = null;
    if (state.futuresConfigurations) {
        try {
            if (!state.futuresConfigurations.length || state.futuresConfigurations.length > 2)
                throw new Error('Select one or two ETF futures routes.');
            const selections = state.futuresConfigurations.map(parseFuturesConfiguration);
            if (
                selections.some((value) => !value.signalRouteId) ||
                new Set(selections.map((value) => value.signalRouteId)).size !== selections.length
            )
                throw new Error('Select distinct saved ETF futures routes.');
            if (
                state.entities.some((entity) => entity.entityType !== 'Asset') ||
                state.screenerEntityTypes.some((type) => type !== 'Asset')
            )
                throw new Error('ETF futures use a stock signal universe.');
            if (
                state.optionBuyingStrategyIds.length ||
                state.tradeSizeDollars.trim() ||
                state.minOptionPrice.trim() ||
                state.partialExitMode !== 'none' ||
                state.priceFillModel !== 'observed_close'
            )
                throw new Error(
                    'ETF futures require configured native sizing, full exits, observed-close conditions and no option selection.'
                );
        } catch (error) {
            futuresError = error instanceof Error ? error.message : 'Invalid ETF futures configuration';
        }
    }
    const blockingReasons = compactReasons([
        futuresError,
        ...buildIdentityBlockingReasons(state, strategyId),
        ...buildFamilyBlockingReasons(state, families),
        ...buildAeroParameterRegistryBlockingReasons({
            activeFamilyKeys: getActiveRegistryFamilyKeys(families),
            registryPlan
        })
    ]);

    if (blockingReasons.length > 0 || strategyId === null) {
        return { input: null, blockingReasons, searchSpace, parameterFamilies };
    }

    return {
        input: buildAeroOptimizationJobInput(state, strategyId, parameterSpaceConfig),
        blockingReasons,
        searchSpace,
        parameterFamilies
    };
};

const resolveActiveFamilies = (state: AeroOptimizationJobInputState): ActiveFamilies => {
    const stopLoss = state.stopLossEnabled && state.stopLossPercentages.length > 0;
    const profitTarget = state.profitTargetEnabled && state.profitTargetPercentages.length > 0;

    return {
        stopLoss,
        trailing:
            stopLoss && state.trailingStopEnabled && state.trailingActivationProfits.length > 0 && state.trailingByPercentages.length > 0,
        profitTarget,
        manualPartials:
            profitTarget && state.partialExitMode === 'manual' && parseManualPartialExits(state.manualPartialExits).pairs.length > 0,
        autoPartials: profitTarget && state.partialExitMode === 'auto' && parseAutoPartialExitsConfig(state.autoPartialExits) !== null,
        profitFloor: state.profitFloorEnabled && parseProfitFloors(state.profitFloors).floors.length > 0,
        maxHoldBars: state.maxHoldBarsEnabled && (state.maxHoldBars.length > 0 || state.includeNoTimeStop)
    };
};

const buildParameterSpaceConfig = (state: AeroOptimizationJobInputState, families: ActiveFamilies): AeroParameterSpaceConfig => {
    const config: AeroParameterSpaceConfig = {
        includeStrategyExitComplement: state.includeStrategyExitComplement
    };

    if (state.priceFillModel === 'resting_level') {
        config.priceFillModel = state.priceFillModel;
    }

    if (families.stopLoss) {
        config.stopLoss = { percentages: state.stopLossPercentages };

        if (state.stopLossBasis === 'requiredCapital') {
            config.stopLoss.basis = state.stopLossBasis;
        }

        if (families.trailing) {
            config.stopLoss.trailing = {
                activationProfits: state.trailingActivationProfits,
                trailByPercentages: state.trailingByPercentages
            };
        }
    }

    if (families.profitTarget) {
        config.profitTarget = { percentages: state.profitTargetPercentages };

        if (families.manualPartials) {
            config.profitTarget.partialExits = parseManualPartialExits(state.manualPartialExits).pairs;
        }

        if (families.autoPartials) {
            const autoConfig = parseAutoPartialExitsConfig(state.autoPartialExits);

            if (autoConfig) config.profitTargetAutoPartialExits = autoConfig;
        }
    }

    if (families.profitFloor) {
        config.profitFloor = { floors: parseProfitFloors(state.profitFloors).floors };
    }

    if (families.maxHoldBars) {
        config.maxHoldBars = {
            bars: state.maxHoldBars,
            includeNoLimit: state.includeNoTimeStop
        };
    }

    const tradeSizeDollars = parseOptionalPositiveNumber(state.tradeSizeDollars);

    if (typeof tradeSizeDollars === 'number') {
        config.tradeSizeDollars = tradeSizeDollars;
    }

    return config;
};

const buildAeroOptimizationJobInput = (
    state: AeroOptimizationJobInputState,
    strategyId: number,
    parameterSpaceConfig: AeroParameterSpaceConfig
): AeroOptimizationJobInput => {
    const input: AeroOptimizationJobInput = {
        strategyId,
        parameterSpaceConfig,
        startDateTime: normalizeMarketDateTime(state.startDateTime) ?? state.startDateTime,
        endDateTime: normalizeMarketDateTime(state.endDateTime) ?? state.endDateTime,
        maxTradeDurationDays: state.futuresConfigurations
            ? 0
            : (parseIntegerInRange(state.maxTradeDurationDays, AERO_MIN_HOLD_DAYS, AERO_MAX_HOLD_DAYS) ?? 90),
        enableDebugLogging: state.enableDebugLogging,
        deleteTradesAfterCompletion: state.futuresConfigurations ? false : state.deleteTradesAfterCompletion,
        optionReturnSelectionPolicy: state.optionReturnSelectionPolicy
    };

    if (state.futuresConfigurations) {
        input.futuresSignalConfigurations = state.futuresConfigurations.map(parseFuturesConfiguration);
        input.maxFailedUnitPercent = 0;
    }
    const jobName = state.jobName.trim();
    const description = state.description.trim();
    const maxFailedUnitPercent = parseOptionalPercent(state.maxFailedUnitPercent);
    const minOptionPrice = parseOptionalNonnegativeNumber(state.minOptionPrice);

    if (jobName) input.name = jobName;
    if (description) input.description = description;
    if (!state.futuresConfigurations && typeof maxFailedUnitPercent === 'number') input.maxFailedUnitPercent = maxFailedUnitPercent;
    if (typeof minOptionPrice === 'number') input.minOptionPrice = minOptionPrice;
    if (state.optionBuyingStrategyIds.length > 0) input.optionBuyingStrategyIds = state.optionBuyingStrategyIds;

    if (state.universeMode === 'screener') {
        if (state.screenerRuleId === null) {
            throw new Error('Screener rule is required for screener universe mode');
        }

        input.baseUniverse = {
            type: 'screenerCandidateUniverse',
            screenerRuleId: state.screenerRuleId,
            ...(state.screenerEntityTypes.length > 0 ? { entityTypes: state.screenerEntityTypes } : {})
        };
    } else {
        input.baseUniverse = {
            type: 'entities',
            entities: state.entities.map((entity) => ({
                entityId: entity.entityId,
                entityType: requireOrdinaryEntityType(entity.entityType)
            }))
        };
    }

    return input;
};

const buildIdentityBlockingReasons = (state: AeroOptimizationJobInputState, strategyId: number | null): (string | null)[] => {
    const normalizedStartDateTime = normalizeMarketDateTime(state.startDateTime);
    const normalizedEndDateTime = normalizeMarketDateTime(state.endDateTime);
    const startDate = normalizedStartDateTime === null ? null : parseMarketDateTime(normalizedStartDateTime);
    const endDate = normalizedEndDateTime === null ? null : parseMarketDateTime(normalizedEndDateTime);

    return [
        optionalReason(strategyId === null, 'Select a strategy to optimize.'),
        optionalReason(state.universeMode === 'entities' && state.entities.length === 0, 'Add at least one entity to simulate.'),
        optionalReason(
            state.universeMode === 'screener' && state.screenerRuleId === null,
            'Select the screener for the candidate universe.'
        ),
        optionalReason(startDate === null, 'Start date/time must use YYYY-MM-DD HH:mm:ss.'),
        optionalReason(endDate === null, 'End date/time must use YYYY-MM-DD HH:mm:ss.'),
        optionalReason(
            startDate !== null && endDate !== null && endDate.getTime() <= startDate.getTime(),
            'End date/time must be after the start date/time.'
        ),
        optionalReason(
            !state.futuresConfigurations &&
                parseIntegerInRange(state.maxTradeDurationDays, AERO_MIN_HOLD_DAYS, AERO_MAX_HOLD_DAYS) === null,
            `Max hold days must be a whole number from ${AERO_MIN_HOLD_DAYS} to ${AERO_MAX_HOLD_DAYS}.`
        ),
        optionalReason(
            state.tradeSizeDollars.trim() !== '' && parseOptionalPositiveNumber(state.tradeSizeDollars) === undefined,
            'Trade size must be a positive dollar amount.'
        ),
        optionalReason(
            state.maxFailedUnitPercent.trim() !== '' && parseOptionalPercent(state.maxFailedUnitPercent) === undefined,
            'Failed-unit tolerance must be a percent from 0 to 100.'
        ),
        optionalReason(
            state.minOptionPrice.trim() !== '' && parseOptionalNonnegativeNumber(state.minOptionPrice) === undefined,
            'Min option price must be a non-negative dollar amount.'
        ),
        optionalReason(
            state.priceFillModel === 'resting_level' && state.optionBuyingStrategyIds.length > 0,
            'Resting-level fills currently support stock jobs only. Remove option strategies or use observed-close fills.'
        )
    ];
};

const buildFamilyBlockingReasons = (state: AeroOptimizationJobInputState, families: ActiveFamilies): (string | null)[] => {
    const reasons: (string | null)[] = [
        optionalReason(
            !families.stopLoss && !families.profitTarget && !families.profitFloor && !families.maxHoldBars,
            'Enable at least one exit type: stop loss, profit target, profit floor, or maximum hold bars.'
        ),
        optionalReason(
            state.stopLossEnabled && state.stopLossPercentages.length === 0,
            'Add a stop-loss percentage or turn off Stop loss.'
        ),
        optionalReason(
            state.stopLossEnabled &&
                state.trailingStopEnabled &&
                (state.trailingActivationProfits.length === 0 || state.trailingByPercentages.length === 0),
            'Trailing stop needs both activation and trail-by percentages.'
        ),
        optionalReason(
            state.stopLossEnabled && state.stopLossBasis === 'requiredCapital' && state.trailingStopEnabled,
            'Required-capital stops are fixed stops and cannot be trailed.'
        ),
        optionalReason(
            state.profitTargetEnabled && state.profitTargetPercentages.length === 0,
            'Add a profit-target percentage or turn off Profit target.'
        ),
        optionalReason(
            state.maxHoldBarsEnabled && state.maxHoldBars.length === 0 && !state.includeNoTimeStop,
            'Add a maximum-hold bar count, include the no-time-stop control, or turn off Maximum hold bars.'
        )
    ];

    if (state.profitTargetEnabled && state.partialExitMode === 'manual') {
        reasons.push(
            optionalReason(
                parseManualPartialExits(state.manualPartialExits).error !== null,
                parseManualPartialExits(state.manualPartialExits).error
            )
        );
    }

    if (state.profitTargetEnabled && state.partialExitMode === 'auto') {
        reasons.push(...buildAutoPartialBlockingReasons(state.autoPartialExits));
    }

    if (state.profitFloorEnabled) {
        const floorParse = parseProfitFloors(state.profitFloors);

        reasons.push(optionalReason(floorParse.error !== null, floorParse.error));

        reasons.push(
            optionalReason(floorParse.error === null && floorParse.floors.length === 0, 'Add a profit floor or turn off Profit floor.')
        );
    }

    return reasons;
};

const buildAutoPartialBlockingReasons = (autoState: AeroAutoPartialExitState): (string | null)[] => {
    const from = parseOptionalNonnegativeNumber(autoState.atProfitFrom);
    const to = parseOptionalPositiveNumber(autoState.atProfitTo);
    const step = parseOptionalPositiveNumber(autoState.atProfitStep);

    return [
        optionalReason(
            from === null || to === null || step === null || from === undefined || to === undefined || step === undefined,
            'Auto partial exits need a profit range: from, to, and step.'
        ),
        optionalReason(
            typeof from === 'number' && typeof to === 'number' && from > to,
            'Auto partial "from" must be less than or equal to "to".'
        ),
        optionalReason(autoState.exitPercentages.length === 0, 'Add at least one value to "Exit percentages to test".'),
        optionalReason(
            autoState.minSpacingPercent.trim() !== '' && parseOptionalNonnegativeNumber(autoState.minSpacingPercent) === undefined,
            'Auto partial minimum spacing must be a non-negative number.'
        ),
        optionalReason(
            autoState.capPerTarget.trim() !== '' && parseOptionalIntegerInRange(autoState.capPerTarget, 1, 1000) === undefined,
            'Auto partial cap per target must be a whole number from 1 to 1000.'
        )
    ];
};

const getActiveRegistryFamilyKeys = (families: ActiveFamilies): AeroParameterFamilySupportKey[] => {
    const keys: AeroParameterFamilySupportKey[] = [];

    if (families.stopLoss) keys.push('stopLoss');
    if (families.profitTarget) keys.push('profitTarget');
    if (families.manualPartials) keys.push('profitTargetPartialExits');
    if (families.autoPartials) keys.push('profitTargetAutoPartialExits');
    if (families.profitFloor) keys.push('profitFloor');
    if (families.maxHoldBars) keys.push('maxHoldBars');

    return keys;
};

const getParameterFamilies = (config: AeroParameterSpaceConfig): string[] => {
    const families: string[] = [];

    if (config.stopLoss) families.push('stopLoss');
    if (config.profitTarget) families.push('profitTarget');
    if (config.profitTargetAutoPartialExits) families.push('profitTargetAutoPartialExits');
    if (config.profitFloor) families.push('profitFloor');
    if (config.maxHoldBars) families.push('maxHoldBars');

    return families;
};

/* -------------------------------------------------------------------------- */
/* Search space estimation                                                    */
/* -------------------------------------------------------------------------- */

const buildSearchSpaceEstimate = (state: AeroOptimizationJobInputState, families: ActiveFamilies): AeroSearchSpaceEstimate => {
    const familyRows: AeroSearchSpaceFamilyRow[] = [];

    const stopLossVariants = families.stopLoss
        ? state.stopLossPercentages.length *
          (families.trailing ? 1 + state.trailingActivationProfits.length * state.trailingByPercentages.length : 1)
        : 1;

    if (families.stopLoss) {
        familyRows.push({
            key: 'stopLoss',
            label: 'Stop loss',
            variants: stopLossVariants,
            detail: families.trailing
                ? `${state.stopLossPercentages.length} level${state.stopLossPercentages.length === 1 ? '' : 's'} × trailing`
                : `${state.stopLossPercentages.length} level${state.stopLossPercentages.length === 1 ? '' : 's'}`
        });
    }

    const profitTargetVariants = families.profitTarget ? estimateProfitTargetVariants(state, families) : 1;

    if (families.profitTarget) {
        familyRows.push({
            key: 'profitTarget',
            label: 'Profit target',
            variants: profitTargetVariants,
            detail: families.autoPartials
                ? 'incl. auto partial-exit ladders'
                : families.manualPartials
                  ? 'with manual partial exits'
                  : `${state.profitTargetPercentages.length} level${state.profitTargetPercentages.length === 1 ? '' : 's'}`
        });
    }

    const profitFloorCount = parseProfitFloors(state.profitFloors).floors.length;
    const profitFloorVariants = families.profitFloor ? profitFloorCount + 1 : 1;

    if (families.profitFloor) {
        familyRows.push({
            key: 'profitFloor',
            label: 'Profit floor',
            variants: profitFloorVariants,
            detail: `${profitFloorCount} floor${profitFloorCount === 1 ? '' : 's'} + no-floor`
        });
    }

    const maxHoldBarsVariants = families.maxHoldBars ? state.maxHoldBars.length + (state.includeNoTimeStop ? 1 : 0) : 1;

    if (families.maxHoldBars) {
        familyRows.push({
            key: 'maxHoldBars',
            label: 'Maximum hold bars',
            variants: maxHoldBarsVariants,
            detail: `${state.maxHoldBars.length} time stop${state.maxHoldBars.length === 1 ? '' : 's'}${state.includeNoTimeStop ? ' + no-limit control' : ''}`
        });
    }

    const hasAnyFamily = families.stopLoss || families.profitTarget || families.profitFloor || families.maxHoldBars;

    const combinations = hasAnyFamily
        ? Math.max(1, stopLossVariants) *
          Math.max(1, profitTargetVariants) *
          Math.max(1, profitFloorVariants) *
          Math.max(1, maxHoldBarsVariants)
        : 0;

    const entityCount = state.universeMode === 'screener' ? null : state.entities.length;
    const optionAxisCount = getAeroOptionAxisCount(state);
    const simulations = entityCount === null ? null : combinations * entityCount * optionAxisCount;
    const tier = resolveSearchSpaceTier(combinations);

    if (state.optionBuyingStrategyIds.length > 0) {
        familyRows.push({
            key: 'optionBuyingStrategies',
            label: 'Option strategies',
            variants: state.optionBuyingStrategyIds.length,
            detail: `${state.optionBuyingStrategyIds.length} OBS ${state.optionBuyingStrategyIds.length === 1 ? 'variant' : 'variants'}`
        });
    }

    return {
        families: familyRows,
        combinations,
        entityCount,
        simulations,
        tier,
        tierLabel: SEARCH_SPACE_TIER_LABELS[tier]
    };
};

const getAeroOptionAxisCount = (state: AeroOptimizationJobInputState) => Math.max(1, state.optionBuyingStrategyIds.length);

const SEARCH_SPACE_TIER_LABELS: Record<AeroSearchSpaceTier, string> = {
    empty: 'No exit types selected',
    quick: 'Quick run',
    standard: 'Standard run',
    large: 'Large — may take a while',
    huge: 'Very large — consider narrowing'
};

const resolveSearchSpaceTier = (combinations: number): AeroSearchSpaceTier => {
    if (combinations <= 0) return 'empty';
    if (combinations < 500) return 'quick';
    if (combinations < 10000) return 'standard';
    if (combinations < 100000) return 'large';

    return 'huge';
};

const estimateProfitTargetVariants = (state: AeroOptimizationJobInputState, families: ActiveFamilies): number => {
    if (families.autoPartials) {
        const autoConfig = parseAutoPartialExitsConfig(state.autoPartialExits);

        if (autoConfig) return estimateAutoPartialProfitTargetVariants(state.profitTargetPercentages, autoConfig);
    }

    return state.profitTargetPercentages.length;
};

const estimateAutoPartialProfitTargetVariants = (
    profitTargetPercentages: number[],
    autoPartialExits: AeroAutoPartialExitsConfig
): number => {
    const atProfitLevels = generateRangeValues(autoPartialExits.atProfit);
    const maxLevels = autoPartialExits.maxLevels ?? DEFAULT_AUTO_PARTIAL_MAX_LEVELS;
    const minSpacing = autoPartialExits.minSpacingPercent ?? DEFAULT_AUTO_PARTIAL_MIN_SPACING_PERCENT;
    const capPerTarget = autoPartialExits.capPerTarget ?? DEFAULT_AUTO_PARTIAL_CAP_PER_TARGET;

    return profitTargetPercentages.reduce((sum, targetPercentage) => {
        const validLevels = atProfitLevels.filter((level) => level < targetPercentage);

        const partialCount = estimateAutoPartialCombinationsForTarget({
            capPerTarget,
            exitPercentages: autoPartialExits.exitPercentages,
            maxLevels,
            minSpacing,
            validLevels
        });

        return sum + 1 + partialCount;
    }, 0);
};

const generateRangeValues = ({ from, step, to }: { from: number; step: number; to: number }): number[] => {
    if (step <= 0 || from > to) return [];

    const values: number[] = [];

    for (let current = from; current <= to + 0.0001 && values.length < MAX_AUTO_PARTIAL_ESTIMATE_LEVELS; current += step) {
        values.push(current);
    }

    return values;
};

/**
 * Expands the auto partial-exit "from / to / step" range into the explicit trigger
 * levels AERO will test, for an at-a-glance preview in the form.
 */
export const buildAutoPartialTriggerPreview = (autoState: AeroAutoPartialExitState): AeroAutoPartialTriggerPreview => {
    const started = autoState.atProfitFrom.trim() !== '' || autoState.atProfitTo.trim() !== '' || autoState.atProfitStep.trim() !== '';

    if (!started) {
        return { triggers: [], truncated: false, error: null, started: false };
    }

    const from = parseOptionalNonnegativeNumber(autoState.atProfitFrom);
    const to = parseOptionalPositiveNumber(autoState.atProfitTo);
    const step = parseOptionalPositiveNumber(autoState.atProfitStep);

    if (typeof from !== 'number' || typeof to !== 'number' || typeof step !== 'number') {
        return {
            triggers: [],
            truncated: false,
            error: 'Enter a valid from, to, and step to define the trigger points.',
            started: true
        };
    }

    if (from > to) {
        return { triggers: [], truncated: false, error: '"From" must be less than or equal to "to".', started: true };
    }

    const triggers = generateRangeValues({ from, step, to });

    return { triggers, truncated: triggers.length >= MAX_AUTO_PARTIAL_ESTIMATE_LEVELS, error: null, started: true };
};

const estimateAutoPartialCombinationsForTarget = ({
    capPerTarget,
    exitPercentages,
    maxLevels,
    minSpacing,
    validLevels
}: {
    capPerTarget: number;
    exitPercentages: number[];
    maxLevels: number;
    minSpacing: number;
    validLevels: number[];
}): number => {
    let count = Math.min(capPerTarget, validLevels.length * exitPercentages.length);

    if (count >= capPerTarget) return count;

    if (maxLevels >= 2) {
        const pairExits = exitPercentages.filter((exitPercentage) => exitPercentage * 2 <= 100);

        count = Math.min(capPerTarget, count + countSpacedLevelPairs(validLevels, minSpacing) * pairExits.length);
    }

    if (count >= capPerTarget) return count;

    if (maxLevels >= 3) {
        const tripleExits = exitPercentages.filter((exitPercentage) => exitPercentage * 3 <= 100);

        count = Math.min(capPerTarget, count + countSpacedLevelTriples(validLevels, minSpacing) * tripleExits.length);
    }

    return count;
};

const countSpacedLevelPairs = (levels: number[], minSpacing: number): number => {
    let count = 0;

    for (let leftIndex = 0; leftIndex < levels.length; leftIndex += 1) {
        for (let rightIndex = leftIndex + 1; rightIndex < levels.length; rightIndex += 1) {
            if (levels[rightIndex] - levels[leftIndex] >= minSpacing) count += 1;
        }
    }

    return count;
};

const countSpacedLevelTriples = (levels: number[], minSpacing: number): number => {
    let count = 0;

    for (let leftIndex = 0; leftIndex < levels.length; leftIndex += 1) {
        for (let middleIndex = leftIndex + 1; middleIndex < levels.length; middleIndex += 1) {
            if (levels[middleIndex] - levels[leftIndex] < minSpacing) continue;

            for (let rightIndex = middleIndex + 1; rightIndex < levels.length; rightIndex += 1) {
                if (levels[rightIndex] - levels[middleIndex] >= minSpacing) count += 1;
            }
        }
    }

    return count;
};

/* -------------------------------------------------------------------------- */
/* Clone hydration                                                            */
/* -------------------------------------------------------------------------- */

export const hydrateAeroStateFromJob = (job: AeroJobDetail): AeroOptimizationJobInputState => {
    const base = createDefaultAeroOptimizationJobInputState();
    const { config } = job;
    const dateWindow = resolveAeroJobDateWindow(job, base);

    const entities: EntityConfig[] = (job.entitiesDetailed ?? []).map((entity) => ({
        entityId: entity.entityId,
        entityType: requireOrdinaryEntityType(entity.entityType),
        symbol: entity.entitySymbol
    }));

    const autoPartial = config.profitTargetAutoPartialExits ?? null;
    const manualPartials = config.profitTarget?.partialExits ?? [];

    const partialExitMode: AeroPartialExitMode = autoPartial ? 'auto' : manualPartials.length > 0 ? 'manual' : 'none';
    const universeScreenerRuleId = job.baseUniverseScreenerRuleId ?? null;

    const universeMode: AeroUniverseMode =
        job.baseUniverseSource === 'screener_candidate' || job.baseUniverseSource === 'resolved_screener_candidate'
            ? 'screener'
            : 'entities';

    return {
        ...base,
        jobName: job.name ? `${job.name} (copy)` : '',
        description: job.description ?? '',
        universeMode,
        entities,
        screenerRuleId: universeScreenerRuleId,
        screenerEntityTypes: universeMode === 'screener' ? deriveScreenerEntityTypesFromEntities(entities) : [],
        startDateTime: dateWindow.startDateTime,
        endDateTime: dateWindow.endDateTime,
        stopLossEnabled: Boolean(config.stopLoss),
        stopLossBasis: config.stopLoss?.basis ?? 'openingPremium',
        stopLossPercentages: config.stopLoss?.percentages ?? [],
        trailingStopEnabled: Boolean(config.stopLoss?.trailing),
        trailingActivationProfits: config.stopLoss?.trailing?.activationProfits ?? [],
        trailingByPercentages: config.stopLoss?.trailing?.trailByPercentages ?? [],
        profitTargetEnabled: Boolean(config.profitTarget),
        profitTargetPercentages: config.profitTarget?.percentages ?? [],
        partialExitMode,
        manualPartialExits: manualPartials.map((pair) => ({
            atProfit: String(pair.atProfit),
            exitPercentage: String(pair.exitPercentage)
        })),
        autoPartialExits: autoPartial
            ? {
                  atProfitFrom: String(autoPartial.atProfit.from),
                  atProfitTo: String(autoPartial.atProfit.to),
                  atProfitStep: String(autoPartial.atProfit.step),
                  exitPercentages: autoPartial.exitPercentages,
                  maxLevels: autoPartial.maxLevels ?? DEFAULT_AUTO_PARTIAL_MAX_LEVELS,
                  minSpacingPercent: autoPartial.minSpacingPercent === undefined ? '' : String(autoPartial.minSpacingPercent),
                  capPerTarget: autoPartial.capPerTarget === undefined ? '' : String(autoPartial.capPerTarget),
                  includeRemainderAtTarget: autoPartial.includeRemainderAtTarget ?? true
              }
            : createDefaultAutoPartialExitState(),
        profitFloorEnabled: Boolean(config.profitFloor),
        profitFloors: (config.profitFloor?.floors ?? []).map((floor) => ({
            triggerPercentage: String(floor.triggerPercentage),
            sellAtPercentage: String(floor.sellAtPercentage)
        })),
        maxHoldBarsEnabled: Boolean(config.maxHoldBars),
        maxHoldBars: config.maxHoldBars?.bars ?? [],
        includeNoTimeStop: config.maxHoldBars?.includeNoLimit ?? true,
        priceFillModel: config.priceFillModel ?? 'observed_close',
        optionBuyingStrategyIds: job.optionBuyingStrategyIds ?? [],
        optionReturnSelectionPolicy: job.optionReturnSelectionPolicy,
        tradeSizeDollars: config.tradeSizeDollars ? String(config.tradeSizeDollars) : '',
        includeStrategyExitComplement: config.includeStrategyExitComplement ?? false,
        maxTradeDurationDays: String(job.maxTradeDurationDays),
        maxFailedUnitPercent:
            job.maxFailedUnitPercent === null || job.maxFailedUnitPercent === undefined ? '' : String(job.maxFailedUnitPercent),
        minOptionPrice: job.minOptionPrice === null || job.minOptionPrice === undefined ? '' : String(job.minOptionPrice)
    };
};

const resolveAeroJobDateWindow = (
    job: AeroJobDetail,
    fallback: Pick<AeroOptimizationJobInputState, 'startDateTime' | 'endDateTime'>
): Pick<AeroOptimizationJobInputState, 'startDateTime' | 'endDateTime'> => ({
    startDateTime:
        normalizeOptionalDateTime(job.windowStartDateTime) ??
        normalizeOptionalDateTime('startDateTime' in job ? job.startDateTime : null) ??
        fallback.startDateTime,
    endDateTime:
        normalizeOptionalDateTime(job.windowEndDateTime) ??
        normalizeOptionalDateTime('endDateTime' in job ? job.endDateTime : null) ??
        fallback.endDateTime
});

const normalizeOptionalDateTime = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;

    const trimmedValue = value.trim();

    return trimmedValue === '' ? null : (normalizeMarketDateTime(trimmedValue) ?? trimmedValue);
};

function deriveScreenerEntityTypesFromEntities(entities: EntityConfig[]): AeroScreenerUniverseEntityType[] {
    const entityTypes = new Set(entities.map((entity) => entity.entityType));
    const supportedTypes = (['Asset', 'Cryptocurrency', 'AssetIndex'] as const).filter((entityType) => entityTypes.has(entityType));

    return supportedTypes.length === 1 ? supportedTypes : [];
}

/* -------------------------------------------------------------------------- */
/* Parsing helpers                                                            */
/* -------------------------------------------------------------------------- */

export const parseManualPartialExits = (
    rows: AeroPartialExitDraft[]
): { pairs: { atProfit: number; exitPercentage: number }[]; error: string | null } => {
    const pairs: { atProfit: number; exitPercentage: number }[] = [];

    for (const row of rows) {
        const atProfitRaw = row.atProfit.trim();
        const exitRaw = row.exitPercentage.trim();

        if (atProfitRaw === '' && exitRaw === '') continue;

        const atProfit = Number(atProfitRaw);
        const exitPercentage = Number(exitRaw);

        if (!Number.isFinite(atProfit) || atProfit <= 0 || !Number.isFinite(exitPercentage) || exitPercentage <= 0) {
            return { pairs: [], error: 'Every partial-exit level needs a positive profit % and exit %.' };
        }

        pairs.push({ atProfit, exitPercentage });
    }

    return { pairs, error: null };
};

export const parseProfitFloors = (
    rows: AeroProfitFloorDraft[]
): { floors: { triggerPercentage: number; sellAtPercentage: number }[]; error: string | null } => {
    const floors: { triggerPercentage: number; sellAtPercentage: number }[] = [];

    for (const row of rows) {
        const triggerRaw = row.triggerPercentage.trim();
        const sellRaw = row.sellAtPercentage.trim();

        if (triggerRaw === '' && sellRaw === '') continue;

        const triggerPercentage = Number(triggerRaw);
        const sellAtPercentage = Number(sellRaw);

        if (!Number.isFinite(triggerPercentage) || triggerPercentage <= 0 || !Number.isFinite(sellAtPercentage) || sellAtPercentage <= 0) {
            return { floors: [], error: 'Every profit floor needs a positive trigger % and exit %.' };
        }

        floors.push({ triggerPercentage, sellAtPercentage });
    }

    return { floors, error: null };
};

const parseAutoPartialExitsConfig = (autoState: AeroAutoPartialExitState): AeroAutoPartialExitsConfig | null => {
    const from = parseOptionalNonnegativeNumber(autoState.atProfitFrom);
    const to = parseOptionalPositiveNumber(autoState.atProfitTo);
    const step = parseOptionalPositiveNumber(autoState.atProfitStep);

    if (
        typeof from !== 'number' ||
        typeof to !== 'number' ||
        typeof step !== 'number' ||
        from > to ||
        autoState.exitPercentages.length === 0
    ) {
        return null;
    }

    const minSpacingPercent = parseOptionalNonnegativeNumber(autoState.minSpacingPercent);
    const capPerTarget = parseOptionalIntegerInRange(autoState.capPerTarget, 1, 1000);

    return {
        atProfit: { from, to, step },
        exitPercentages: autoState.exitPercentages,
        maxLevels: autoState.maxLevels,
        minSpacingPercent: typeof minSpacingPercent === 'number' ? minSpacingPercent : DEFAULT_AUTO_PARTIAL_MIN_SPACING_PERCENT,
        capPerTarget: typeof capPerTarget === 'number' ? capPerTarget : DEFAULT_AUTO_PARTIAL_CAP_PER_TARGET,
        includeRemainderAtTarget: autoState.includeRemainderAtTarget
    };
};

const optionalReason = (condition: boolean, reason: string | null): string | null => (condition ? reason : null);

const compactReasons = (reasons: (string | null)[]): string[] => {
    const seen = new Set<string>();
    const result: string[] = [];

    for (const reason of reasons) {
        if (reason === null || seen.has(reason)) continue;

        seen.add(reason);
        result.push(reason);
    }

    return result;
};

const parseOptionalIntegerInRange = (rawValue: string, min: number, max: number): number | null | undefined => {
    const trimmed = rawValue.trim();

    if (!trimmed) return null;

    const value = Number(trimmed);

    return Number.isInteger(value) && value >= min && value <= max ? value : undefined;
};

const parseIntegerInRange = (rawValue: string, min: number, max: number): number | null => {
    const value = Number(rawValue.trim());

    return Number.isInteger(value) && value >= min && value <= max ? value : null;
};

const parseOptionalPositiveNumber = (rawValue: string): number | null | undefined => {
    const trimmed = rawValue.trim();

    if (!trimmed) return null;

    const value = Number(trimmed);

    return Number.isFinite(value) && value > 0 ? value : undefined;
};

const parseOptionalPercent = (rawValue: string): number | null | undefined => {
    const trimmed = rawValue.trim();

    if (!trimmed) return null;

    const value = Number(trimmed);

    return Number.isFinite(value) && value >= 0 && value <= 100 ? value : undefined;
};

const parseOptionalNonnegativeNumber = (rawValue: string): number | null | undefined => {
    const trimmed = rawValue.trim();

    if (!trimmed) return null;

    const value = Number(trimmed);

    return Number.isFinite(value) && value >= 0 ? value : undefined;
};

const normalizeMarketDateTime = (value: string): string | null => {
    const trimmedValue = value.trim();

    if (MARKET_DATE_TIME_PATTERN.test(trimmedValue)) {
        return parseMarketDateTime(trimmedValue) === null ? null : trimmedValue;
    }

    if (MARKET_DATE_TIME_WITHOUT_SECONDS_PATTERN.test(trimmedValue)) {
        const normalizedValue = `${trimmedValue}:00`;

        return parseMarketDateTime(normalizedValue) === null ? null : normalizedValue;
    }

    if (MARKET_DATE_PATTERN.test(trimmedValue)) {
        const normalizedValue = `${trimmedValue} 00:00:00`;

        return parseMarketDateTime(normalizedValue) === null ? null : normalizedValue;
    }

    return null;
};

const parseMarketDateTime = (value: string): Date | null => {
    if (!MARKET_DATE_TIME_PATTERN.test(value)) return null;

    const parsedDate = new Date(value.replace(' ', 'T'));

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};
